import WhatsNewRailButton from '@/app/WhatsNewRailButton';

// import types
import type {NavigationRailMenuData} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';

type WhatsNewOptions = {
    // The "Nieuw" button (rail and menu item) is only shown to administrators.
    isAdmin: boolean;
    label: string;
};

// Below "expanded" the rail is replaced by the hamburger menu, which shows the same data but no
// actionButton slot, so "Nieuw" becomes a menu item there.
// The menu walks every item recursively, so label and sx must be plain values (a React element as
// label overflows the stack); the dot is drawn with ::after. The menu list has no footer slot, so
// the item turns its own list into a full-height column (:has) and pushes itself to the bottom
// with margin-top auto.
export const getWhatsNewMenuData = (
    navData: NavigationRailMenuData,
    {
        isAdmin,
        label,
        isSmallScreen,
        hasNew,
        onOpen,
    }: WhatsNewOptions & {isSmallScreen: boolean; hasNew: boolean; onOpen: () => void},
): NavigationRailMenuData => {
    // An empty list means the menu is still loading; don't show "Nieuw" on its own meanwhile.
    if (!isAdmin || !isSmallScreen || !navData.length) return navData;

    return [
        ...navData,
        {
            id: 'whats-new',
            label,
            iconType: 'gift',
            onClick: onOpen,
            'data-testid': 'WhatsNewMenuItem',
            sx: {
                color: 'primary.main',
                fontWeight: 600,
                flexGrow: 0,
                mt: 'auto',
                'ul:has(> &)': {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                    boxSizing: 'border-box',
                    // ListItemButton grows by default; keep the items their own height
                    '& > *': {flexGrow: 0},
                },
                ...(hasNew && {
                    '&::after': {
                        content: '""',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        flexShrink: 0,
                    },
                }),
            },
        } as NavigationRailMenuData[number],
    ];
};

// The rail only renders the actionButton slot when it has children. The mobile menu ignores slots
// and renders its own footer button from these props; hide that one, because "Nieuw" is a menu
// item there (getWhatsNewMenuData).
export const getWhatsNewRailProps = ({isAdmin, label}: WhatsNewOptions) =>
    isAdmin
        ? {
              slots: {actionButton: WhatsNewRailButton},
              slotProps: {
                  actionButton: {
                      children: label,
                      slotProps: {iconButton: {sx: {display: 'none'}}},
                  },
              },
          }
        : {};
